import React from 'react';
const { useState, useEffect } = React;
import { F } from '../utils.jsx';
import { ROLES, DIAGNOSTIC_STATUSES, RUBRIC_VERSION, t } from '../diagnosticConfig.js';
import { adminCall, Field, SubTabs, inputSt, labelSt, cardSt, badgeSt, warnSt } from './diagnostic/shared.jsx';
import DiagnosticSetup from './diagnostic/DiagnosticSetup.jsx';
import SessionsPanel from './diagnostic/SessionsPanel.jsx';
import CrmImport from './diagnostic/CrmImport.jsx';
import TeamInsights from './diagnostic/TeamInsights.jsx';
import AiPipeline from './diagnostic/AiPipeline.jsx';
import Deliverables from './diagnostic/Deliverables.jsx';
import PersonPicker from './people/PersonPicker.jsx';
import OriginNote from './serviceStart/OriginNote.jsx';
import SystemDesign from './diagnostic/SystemDesign.jsx';

// Oike Diagnostic admin tab: create a diagnostic, add participants, copy their
// links, extend deadlines and follow form status; set up the role-play, record
// the 1:1 sessions and transcripts, import CRM metrics and see the team
// aggregates, and run the AI pipeline (P1-P5) with Ale's validation. Entregables:
// edit, approve and publish the profiles and the report, the follow-up proposal,
// closing the diagnostic and deleting a test one. System Design (Fase 7): the
// modules, the playbook and the final measurement, enabled with P4 approved.
// Talks only to /api/diagnostic-admin (Oike administrator).

const FORM_LABELS = { self: 'Autoevaluación', manager: 'Manager', inventory: 'Inventario' };
const STATUS_LABELS = { not_started: 'Sin empezar', draft: 'En curso', submitted: 'Enviado' };
const DEFAULT_FORMS = { founder_seller: ['self', 'inventory'], manager: ['manager'] };

function addDays(isoDate, days) {
  const d = isoDate ? new Date(`${isoDate}T12:00:00Z`) : new Date();
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Diagnostics of the same account scored with different rubric versions (the
 * version their P4 was approved with): their levels are not comparable one to one.
 */
function RubricCompareWarning({ list }) {
  const versions = [...new Set(list.map(d => d.rubricVersion).filter(Boolean))];
  if (versions.length < 2) return null;
  return (
    <div style={{ ...warnSt, fontSize: 13 }}>
      ⚠️ Los diagnósticos de esta empresa se puntuaron con versiones distintas de la rúbrica ({versions.map(v => `v${v}`).join(', ')}; la vigente es v{RUBRIC_VERSION}).
      Si los comparas, ten en cuenta que un mismo nivel puede no significar lo mismo.
      <div style={{ fontSize: 12, marginTop: 4, color: 'var(--globant-muted)' }}>
        {list.filter(d => d.rubricVersion).map(d => `${d.name}: v${d.rubricVersion}`).join(' · ')}
      </div>
    </div>
  );
}

export default function AccountDiagnosticTab({ account }) {
  const [list, setList] = useState(null);
  const [current, setCurrent] = useState(null); // { diagnostic, participants }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  const run = async (fn) => {
    setBusy(true); setError('');
    try { await fn(); } catch (e) { setError(e.message || 'Error'); }
    setBusy(false);
  };
  // Refresh the open diagnostic without touching busy/error (used inside run)
  const reload = async () => { if (current) setCurrent(await adminCall({ action: 'get', diagnosticId: current.diagnostic.id })); };

  const loadList = () => run(async () => {
    const { diagnostics } = await adminCall({ action: 'list', accountId: account.id });
    setList(diagnostics);
    // Arriving from "Preparar inicio del servicio": that diagnostic first
    const pending = window.__oikePendingDiagnostic; window.__oikePendingDiagnostic = null;
    const first = diagnostics.find(d => d.id === pending) || diagnostics[0];
    if (first && (!current || pending)) setCurrent(await adminCall({ action: 'get', diagnosticId: first.id }));
  });

  useEffect(() => { setCurrent(null); setList(null); loadList(); }, [account?.id]);

  const open = (id) => run(async () => setCurrent(await adminCall({ action: 'get', diagnosticId: id })));

  if (list === null) return <div style={{ color: 'var(--globant-muted)', padding: 20 }}>{busy ? '⏳ Cargando…' : error}</div>;

  return (
    <div>
      {error && <div style={{ ...cardSt, borderColor: 'rgba(239,68,68,0.4)', color: '#B4362C', fontSize: 13 }}>⚠️ {error}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        {list.map(d => (
          <button key={d.id} className="action-btn" onClick={() => open(d.id)}
            style={{ fontSize: 12, padding: '6px 12px', ...(current?.diagnostic.id === d.id ? { borderColor: 'var(--globant-green)', color: 'var(--globant-green)' } : {}) }}>
            {d.name}{d.isTest ? ' · 🧪 Prueba' : ''}{d.rubricVersion ? ` · rúbrica v${d.rubricVersion}` : ''}
          </button>
        ))}
        <button className="action-btn btn-primary" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => setCreating(c => !c)} disabled={busy}>
          {creating ? 'Cancelar' : '➕ Nuevo diagnóstico'}
        </button>
      </div>

      <RubricCompareWarning list={list} />

      {creating && <CreateForm account={account} busy={busy} onCreate={(input) => run(async () => {
        const { diagnostic } = await adminCall({ action: 'create', accountId: account.id, ...input });
        setCreating(false);
        setList(l => [diagnostic, ...(l || [])]);
        setCurrent(await adminCall({ action: 'get', diagnosticId: diagnostic.id }));
      })} />}

      {!creating && !current && !list.length && (
        <div style={{ ...cardSt, color: 'var(--globant-muted)', fontSize: 13 }}>Esta empresa todavía no tiene un diagnóstico.</div>
      )}

      {current && !creating && (
        <DiagnosticDetail key={current.diagnostic.id} data={current} busy={busy} run={run} reload={reload}
          onSaveCrm={async (metrics) => {
            let ok = false;
            await run(async () => { setCurrent(await adminCall({ action: 'saveCrmMetrics', diagnosticId: current.diagnostic.id, metrics })); ok = true; });
            return ok;
          }}
          onUpdate={(fields) => run(async () => setCurrent(await adminCall({ action: 'update', diagnosticId: current.diagnostic.id, ...fields })))}
          onAddParticipant={(p) => run(async () => setCurrent(await adminCall({ action: 'addParticipant', diagnosticId: current.diagnostic.id, ...p })))}
          onUpdateParticipant={(id, fields) => run(async () => setCurrent(await adminCall({ action: 'updateParticipant', participantId: id, ...fields })))}
          onExclude={(id, excluded) => run(async () => setCurrent(await adminCall({ action: 'setExcluded', participantId: id, excluded })))}
          onDeleted={() => { const id = current.diagnostic.id; setCurrent(null); setList(l => (l || []).filter(x => x.id !== id)); }}
          onRefresh={() => open(current.diagnostic.id)} />
      )}
    </div>
  );
}

function CreateForm({ account, busy, onCreate }) {
  const month = new Date().toLocaleDateString('es', { month: 'short', year: 'numeric' });
  const [f, setF] = useState({
    name: `${F(account, 'Account Name') || 'Diagnóstico'} — ${month}`, isTest: false, language: 'es',
    formsDueAt: addDays(null, 7), founderGoal: '', exerciseAccountName: '', exerciseAccountWebsite: '', exerciseAccountCountry: '',
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  return (
    <div style={cardSt}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <Field label="Nombre"><input style={inputSt} value={f.name} onChange={set('name')} /></Field>
        <Field label="Fecha límite de formularios"><input type="date" style={inputSt} value={f.formsDueAt} onChange={set('formsDueAt')} /></Field>
        <Field label="Cuenta del ejercicio"><input style={inputSt} value={f.exerciseAccountName} onChange={set('exerciseAccountName')} /></Field>
        <Field label="Web de la cuenta del ejercicio"><input style={inputSt} value={f.exerciseAccountWebsite} onChange={set('exerciseAccountWebsite')} /></Field>
        <Field label="País de la cuenta del ejercicio"><input style={inputSt} value={f.exerciseAccountCountry} onChange={set('exerciseAccountCountry')} /></Field>
      </div>
      <div style={{ marginTop: 12 }}>
        <Field label="Meta del founder"><textarea style={{ ...inputSt, minHeight: 60, fontFamily: 'inherit' }} value={f.founderGoal} onChange={set('founderGoal')} /></Field>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={f.isTest} onChange={set('isTest')} /> 🧪 Es de prueba
        </label>
        <button className="action-btn btn-primary" disabled={busy || !f.name.trim()} onClick={() => onCreate(f)}>
          {busy ? '⏳' : 'Crear diagnóstico'}
        </button>
      </div>
    </div>
  );
}

const TABS = [['setup', 'Configuración'], ['people', 'Participantes'], ['sessions', 'Sesiones 1:1'], ['crm', 'CRM'], ['team', 'Equipo'], ['ai', 'IA'], ['deliverables', 'Entregables'], ['systemDesign', 'System Design']];

function DiagnosticDetail({ data, busy, run, reload, onUpdate, onSaveCrm, onAddParticipant, onUpdateParticipant, onExclude, onDeleted, onRefresh }) {
  const d = data.diagnostic;
  const [tab, setTab] = useState('setup');
  const [edit, setEdit] = useState({ status: d.status, formsOpenAt: d.formsOpenAt || '', formsDueAt: d.formsDueAt || '' });
  const set = (k) => (e) => setEdit({ ...edit, [k]: e.target.value });
  const changed = Object.keys(edit).some(k => (edit[k] || '') !== (d[k] || ''));
  // One access per person: the person's personal link, pointed to this diagnostic. Only the coach gets it
  // (the server refuses anyone else), and there is no other link to send instead: the form link is not shown.
  const copyFor = async (p) => {
    if (!p.personId) { window.alert('Primero crea o vincula su ficha (abajo, en esta misma fila): su acceso es el de su ficha.'); return; }
    try {
      let r = await adminCall({ action: 'personAccess', participantId: p.id });
      if (r.never && window.confirm('Todavía no tiene link personal. ¿Crearlo ahora?')) r = await adminCall({ action: 'personAccess', op: 'create', participantId: p.id });
      if (r.link) { copy(r.link); return; }
      if (r.never) return;
      window.alert(r.review ? 'Su acceso está para revisar (ficha anterior). Créalo o déjalo sin acceso desde su ficha en Personas.' : 'El acceso personal de esta persona está revocado. Renuévalo desde su ficha en Personas.');
    } catch (e) {
      window.alert(e.code === 'Forbidden' ? 'Solo la coach puede obtener el acceso personal de una persona.' : e.message);
    }
  };
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); window.__oikeToast?.('Link copiado', 'success'); }
    catch { window.prompt('Copia el link:', text); }
  };

  return (
    <div>
      <div style={cardSt}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{d.name} {d.isTest && <span style={badgeSt(false)}>🧪 Prueba</span>}</div>
          <button className="action-btn" style={{ fontSize: 12 }} onClick={onRefresh} disabled={busy}>↻ Actualizar</button>
        </div>
        <OriginNote origins={d.serviceOrigins} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <Field label="Estado">
            <select style={inputSt} value={edit.status} onChange={set('status')}>
              {DIAGNOSTIC_STATUSES.map(s => <option key={s.key} value={s.key}>{t(s.label)}</option>)}
            </select>
          </Field>
          <Field label="Apertura de formularios"><input type="date" style={inputSt} value={edit.formsOpenAt} onChange={set('formsOpenAt')} /></Field>
          <Field label="Fecha límite (+3 días de gracia)">
            <div style={{ display: 'flex', gap: 6 }}>
              <input type="date" style={inputSt} value={edit.formsDueAt} onChange={set('formsDueAt')} />
              <button className="action-btn" style={{ fontSize: 11, whiteSpace: 'nowrap' }} title="Extender 7 días"
                onClick={() => setEdit({ ...edit, formsDueAt: addDays(edit.formsDueAt || null, 7) })}>+7 días</button>
            </div>
          </Field>
        </div>
        {d.status === 'setup' && <div style={{ fontSize: 12, color: 'var(--globant-muted)', marginTop: 10 }}>Los links no funcionan hasta pasar el estado a "Formularios abiertos".</div>}
        <div style={{ textAlign: 'right', marginTop: 12 }}>
          <button className="action-btn btn-primary" disabled={busy || !changed}
            onClick={() => onUpdate({ ...edit, formsOpenAt: edit.formsOpenAt || null, formsDueAt: edit.formsDueAt || null })}>
            {busy ? '⏳' : '💾 Guardar cambios'}
          </button>
        </div>
      </div>

      <SubTabs tabs={TABS} value={tab} onChange={setTab} />

      {tab === 'setup' && <DiagnosticSetup diagnostic={d} busy={busy} onUpdate={onUpdate} />}

      {tab === 'people' && (
        <div style={cardSt}>
          <div style={{ ...labelSt, marginBottom: 12 }}>Participantes ({data.participants.length})</div>
          <PeopleSuggestions diagnosticId={d.id} participants={data.participants} run={run} reload={reload} />
          {data.participants.map(p => (
            <ParticipantRow key={p.id} p={p} busy={busy} defaultDue={d.formsDueAt} closed={d.status === 'closed' || !!d.closedAt}
              delivered={!!d.reportDeliveredAt} diagnosticId={d.id} run={run} reload={reload}
              taken={data.participants.map(x => x.personId).filter(Boolean)}
              onCopy={() => copyFor(p)} onUpdate={(fields) => onUpdateParticipant(p.id, fields)}
              onExclude={(excluded) => onExclude(p.id, excluded)} />
          ))}
          <AddParticipant busy={busy} diagnosticId={d.id} taken={data.participants.map(x => x.personId).filter(Boolean)} onAdd={onAddParticipant} />
        </div>
      )}

      {tab === 'sessions' && <SessionsPanel participants={data.participants} run={run} onChanged={reload} />}
      {tab === 'crm' && <CrmImport diagnostic={d} participants={data.participants} busy={busy} onSave={onSaveCrm} />}
      {tab === 'team' && <TeamInsights diagnosticId={d.id} run={run} />}
      {tab === 'ai' && <AiPipeline diagnosticId={d.id} run={run} busy={busy} />}
      {tab === 'deliverables' && <Deliverables diagnosticId={d.id} run={run} busy={busy} onDeleted={onDeleted} />}
      {tab === 'systemDesign' && <SystemDesign diagnosticId={d.id} run={run} busy={busy} />}
    </div>
  );
}

const EXCLUDE_TEXT = (name) => `¿Excluir a ${name} del diagnóstico?\n\nNo se borra nada: queda marcada como excluida, deja de bloquear P4, no cuenta en los agregados ni en el tamaño del equipo, y su link deja de aceptar respuestas. Se puede volver a incluir mientras el diagnóstico no esté cerrado.`;

/** Server calls of the person picker, from Diagnostic (basic data of the cards only). */
export const diagnosticPeopleApi = (diagnosticId) => ({
  search: async (q) => (await adminCall({ action: 'personSearch', q })).people,
  create: async (person, { confirmNew, stakeholderId } = {}) => (await adminCall({ action: 'createPerson', person, confirmNew, stakeholderId })).person,
  contacts: async (q) => (await adminCall({ action: 'contactSearch', diagnosticId, q })).contacts,
  fromContact: (stakeholderId) => adminCall({ action: 'personFromContact', stakeholderId }),
  linkContact: async (personId, stakeholderId) => (await adminCall({ action: 'linkPersonContact', personId, stakeholderId })).person,
});

/** Participants without a card whose email matches one: suggestions only, linked with a click. */
function PeopleSuggestions({ diagnosticId, participants, run, reload }) {
  const [list, setList] = useState([]);
  const pending = participants.filter(p => !p.personId && p.email).map(p => p.id).join(',');
  useEffect(() => { adminCall({ action: 'personSuggestions', diagnosticId }).then(d => setList(d.suggestions)).catch(() => setList([])); }, [diagnosticId, pending]);
  if (!list.length) return null;
  const nameOf = (id) => participants.find(p => p.id === id)?.name || '';
  return (
    <div style={{ ...warnSt, fontSize: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>Fichas con el mismo email (sugerencias)</div>
      <div style={{ color: 'var(--globant-muted)', marginBottom: 6 }}>Nada se vincula solo. Vincular no cambia el nombre ni el email que guarda este diagnóstico.</div>
      {list.map(s => s.matches.map(m => (
        <div key={`${s.participantId}-${m.id}`} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '4px 0' }}>
          <span><b>{nameOf(s.participantId)}</b> ↔ ficha <b>{m.name}</b> · {m.email}</span>
          <button className="action-btn" style={{ fontSize: 11 }} onClick={() => run(async () => { await adminCall({ action: 'linkParticipantPerson', participantId: s.participantId, personId: m.id }); await reload(); })}>Vincular</button>
        </div>
      )))}
    </div>
  );
}

const STATUS_NAME = Object.fromEntries(DIAGNOSTIC_STATUSES.map(s => [s.key, s.label]));

/** The identity card of a participant, apart from what this diagnostic holds about them. */
function PersonCard({ p, delivered, closed, diagnosticId, taken, run, reload }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [picking, setPicking] = useState(false);
  const loadHistory = () => adminCall({ action: 'personDiagnostics', personId: p.personId }).then(setHistory).catch(() => setHistory({ diagnostics: [] }));
  if (!p.personId) {
    return (
      <div style={{ fontSize: 11, color: 'var(--globant-muted)', flexBasis: '100%' }}>
        🪪 Sin ficha de persona.{' '}
        {!picking ? <button className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setPicking(true)}>Elegir o crear ficha</button> : (
          <div style={{ marginTop: 6, maxWidth: 560 }}>
            <PersonPicker api={diagnosticPeopleApi(diagnosticId)} exclude={taken}
              onPick={(card) => run(async () => { await adminCall({ action: 'linkParticipantPerson', participantId: p.id, personId: card.id }); setPicking(false); await reload(); })} />
            <button className="action-btn" style={{ fontSize: 11, marginTop: 6 }} onClick={() => setPicking(false)}>Cancelar</button>
          </div>
        )}
      </div>
    );
  }
  const card = p.person || {};
  const differs = !card.missing && (card.name !== p.name || (card.email || '') !== (p.email || '').toLowerCase());
  return (
    <div style={{ fontSize: 11, color: 'var(--globant-muted)', flexBasis: '100%' }}>
      🪪 Ficha: {card.missing ? 'no se encontró (¿se borró?)' : <><b style={{ color: 'var(--globant-text)' }}>{card.name}</b>{card.email ? ` · ${card.email}` : ''}</>}
      {' '}<button className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => { setOpen(o => !o); if (!history) loadHistory(); }}>{open ? 'Ocultar diagnósticos' : 'Sus diagnósticos'}</button>
      {differs && (
        <div style={{ marginTop: 4 }}>
          La ficha cambió: este diagnóstico conserva «{p.name}{p.email ? ` · ${p.email}` : ''}».{' '}
          {!delivered && !closed && <button className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => run(async () => { await adminCall({ action: 'copyPersonToParticipant', participantId: p.id }); await reload(); })}>Usar los datos de la ficha aquí</button>}
          {(delivered || closed) && ' Con el informe entregado se conserva como estaba.'}
        </div>
      )}
      {open && (
        <div style={{ marginTop: 6, paddingLeft: 8, borderLeft: '2px solid var(--globant-border)' }}>
          {!history ? 'Cargando…' : !history.diagnostics.length ? 'Ninguno.' : history.diagnostics.map(x => (
            <div key={x.participantId}>{x.diagnosticName} · {x.accountName || 'sin empresa'} · {t(ROLES[x.role]?.label) || x.role} · {t(STATUS_NAME[x.status]) || x.status}{x.isTest ? ' · prueba' : ''}{x.excluded ? ' · excluida' : ''}{x.diagnosticId === diagnosticId ? ' · este' : ''}</div>
          ))}
          <div style={{ marginTop: 4 }}>Solo participaciones y datos básicos: las respuestas, sesiones y evaluaciones quedan en cada diagnóstico.</div>
        </div>
      )}
    </div>
  );
}

function ParticipantRow({ p, busy, defaultDue, closed, delivered, diagnosticId, taken, run, reload, onCopy, onUpdate, onExclude }) {
  const [due, setDue] = useState(p.dueAtOverride || '');
  return (
    <div style={{ borderBottom: '1px solid var(--globant-border)', padding: '10px 0', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', ...(p.excluded ? { opacity: 0.6 } : {}) }}>
      <div style={{ flex: '1 1 200px' }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name} {p.excluded && <span style={{ ...badgeSt(false), color: '#9A6212' }} title={`Excluida el ${p.excludedAt ? new Date(p.excludedAt).toLocaleDateString('es') : '—'}`}>Excluida</span>}</div>
        <div style={{ fontSize: 11, color: 'var(--globant-muted)' }}>
          {t(ROLES[p.role]?.label) || p.role}{p.email ? ` · ${p.email}` : ''} · {p.consent ? '✓ consentimiento' : 'sin consentimiento'}
          {p.motivatorsNamed ? ' · motivadores con nombre' : ''}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {p.forms.map(f => <span key={f} style={badgeSt(p.formStatus[f] === 'submitted')}>{FORM_LABELS[f]}: {STATUS_LABELS[p.formStatus[f]] || '—'}</span>)}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="date" style={{ ...inputSt, width: 150 }} value={due} title={`Plazo propio (por defecto: ${defaultDue || 'sin fecha'})`}
          onChange={e => setDue(e.target.value)} />
        {due !== (p.dueAtOverride || '') && (
          <button className="action-btn" style={{ fontSize: 11 }} disabled={busy} onClick={() => onUpdate({ dueAtOverride: due || null })}>Guardar plazo</button>
        )}
        {!p.excluded && p.personId && <button className="action-btn" style={{ fontSize: 11 }} onClick={onCopy} title="Su link personal: el mismo en todos los módulos">🔗 Copiar link de la persona</button>}
        {!closed && (p.excluded
          ? <button className="action-btn" style={{ fontSize: 11 }} disabled={busy} onClick={() => onExclude(false)}>↩ Volver a incluir</button>
          : <button className="action-btn" style={{ fontSize: 11, color: '#9A6212' }} disabled={busy} onClick={() => window.confirm(EXCLUDE_TEXT(p.name)) && onExclude(true)}>Excluir</button>)}
      </div>
      <PersonCard p={p} delivered={delivered} closed={closed} diagnosticId={diagnosticId} taken={taken} run={run} reload={reload} />
    </div>
  );
}

function AddParticipant({ busy, diagnosticId, taken, onAdd }) {
  const [card, setCard] = useState(null);
  const [role, setRoleKey] = useState('ae');
  const [forms, setForms] = useState(['self']);
  const setRole = (r) => { setRoleKey(r); setForms(DEFAULT_FORMS[r] || ['self']); };
  const toggleForm = (f) => setForms(forms.includes(f) ? forms.filter(x => x !== f) : [...forms, f]);
  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--globant-border)', paddingTop: 12 }}>
      <div style={{ ...labelSt, marginBottom: 6 }}>Agregar participante</div>
      {!card ? (
        <div style={{ maxWidth: 560 }}>
          <PersonPicker api={diagnosticPeopleApi(diagnosticId)} exclude={taken} onPick={setCard} />
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 220px', fontSize: 13 }}>
            🪪 <b>{card.name}</b>{card.email ? ` · ${card.email}` : ''}{' '}
            <button className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setCard(null)}>Cambiar</button>
            <div style={{ fontSize: 11, color: 'var(--globant-muted)' }}>Este diagnóstico guarda su propia copia del nombre y el email.</div>
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <Field label="Rol">
              <select style={inputSt} value={role} onChange={e => setRole(e.target.value)}>
                {Object.entries(ROLES).map(([k, r]) => <option key={k} value={k}>{t(r.label)}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 10, fontSize: 12, paddingBottom: 8 }}>
            {Object.entries(FORM_LABELS).map(([f, label]) => (
              <label key={f} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input type="checkbox" checked={forms.includes(f)} onChange={() => toggleForm(f)} /> {label}
              </label>
            ))}
          </div>
          <button className="action-btn btn-primary" disabled={busy || !forms.length}
            onClick={() => { onAdd({ personId: card.id, role, forms }); setCard(null); }}>
            ➕ Agregar
          </button>
        </div>
      )}
    </div>
  );
}
